
import React, { useEffect, useRef, useState } from 'react';

// MapPage: uses Leaflet (loaded dynamically) to show OpenStreetMap tiles, user location, and place markers.
// It fetches places from the CRM `/api/places` endpoint and displays markers grouped by category.
const CRM_API_BASE = process.env.REACT_APP_CRM_API_URL || 'http://localhost:6001/api';

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);
    // load CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    // load script
    if (!document.querySelector('script[src*="leaflet.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      script.onload = () => resolve(window.L);
      script.onerror = (e) => reject(new Error('Failed to load Leaflet'));
      document.body.appendChild(script);
    } else {
      // already present but window.L may not be ready yet
      const wait = setInterval(() => { if (window.L) { clearInterval(wait); resolve(window.L); } }, 50);
      setTimeout(() => { clearInterval(wait); reject(new Error('Leaflet load timeout')); }, 5000);
    }
  });
}

export default function MapPage() {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPos, setUserPos] = useState(null);
  
  // Try to read the selected category from localStorage (set by NewHome)
  const getInitialCategory = () => {
    try {
      const saved = localStorage.getItem('zz_map_category');
      return saved || 'restaurants';
    } catch (e) {
      return 'restaurants';
    }
  };
  
  const [category, setCategory] = useState(getInitialCategory());
  const [placesRadius, setPlacesRadius] = useState(2000);
  const [isFetching, setIsFetching] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const firstRunRef = useRef(true);
  // Use a ref to hold marker instances so we can synchronously remove them when refreshing
  const markersRef = useRef({});

  const FIXED_PLACE_CATEGORIES = ['restaurants','coffee','stores','pharmacy','parks','beach'];

  // Convert Leaflet zoom level to approximate radius (meters) for Overpass/query filtering
  const zoomToRadius = (zoom) => {
    // Leaflet zoom: higher value = closer in. Map to conservative radii.
    if (zoom >= 17) return 300;
    if (zoom === 16) return 600;
    if (zoom === 15) return 1000;
    if (zoom === 14) return 1600;
    if (zoom === 13) return 2600;
    if (zoom === 12) return 4500;
    if (zoom === 11) return 9000;
    if (zoom === 10) return 16000;
    return 30000;
  };

  useEffect(() => {
    let mounted = true;
    loadLeaflet().then(L => {
      if (!mounted) return;
      leafletRef.current = L;
      const el = document.getElementById('zz-osm-map');
      if (!el) return;
      mapRef.current = L.map(el, { center: [45.5, -73.6], zoom: 13, zoomControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapRef.current);

      // try to locate user
      if (navigator && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(pos => {
          const lat = pos.coords.latitude; const lon = pos.coords.longitude;
          setUserPos({ lat, lon });
          if (mapRef.current) mapRef.current.setView([lat, lon], 14);
          const marker = L.circleMarker([lat, lon], { radius: 8, fillColor: '#007AFF', color: '#fff', weight: 2, fillOpacity: 0.9 }).addTo(mapRef.current);
          marker.bindPopup('You are here');
        }, () => {}, { enableHighAccuracy: true, timeout: 10000 });
      }

      setLoading(false);
    }).catch(err => { setError(err.message || String(err)); setLoading(false); });
    return () => { mounted = false; if (mapRef.current) mapRef.current.remove(); };
  }, []);

  // Local client-side cache helpers (mirror PostLoginHome cache format)
  const PLACES_CACHE_TTL = 60 * 30; // seconds
  const cacheKeyFor = (cat, lat, lon, radius) => `zz_places_cache:${cat}:${Math.round((lat||0)*10000)}:${Math.round((lon||0)*10000)}:${radius}`;
  const readCache = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const j = JSON.parse(raw);
      if (!j.ts || !j.data) return null;
      const age = (Date.now() - j.ts) / 1000;
      if (age > PLACES_CACHE_TTL) return null;
      return j.data;
    } catch (e) { return null; }
  };
  const writeCache = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch (e) { /* ignore */ }
  };

  // fetch places for selected category and add markers
  useEffect(() => {
    if (!leafletRef.current || !mapRef.current) return;
    let cancelled = false;
    const L = leafletRef.current;
    const clearMarkers = () => {
      try {
        Object.values(markersRef.current || {}).forEach(m => { try { mapRef.current.removeLayer(m); } catch (e) {} });
      } catch (e) {}
      markersRef.current = {};
    };

    // prevent rapid repeated network calls; we'll debounce map interactions
    let fetchSeq = 0;
    let debounceTimer = null;

    const scheduleLoad = () => {
      try { if (debounceTimer) clearTimeout(debounceTimer); } catch (e) {}
      debounceTimer = setTimeout(() => { debounceTimer = null; loadPlaces(); }, 350);
    };

    const loadPlaces = async () => {
      try {
  const center = mapRef.current.getCenter();
  const lat = center.lat; const lon = center.lng;
  const zoom = mapRef.current.getZoom ? mapRef.current.getZoom() : 13;
  const radius = zoomToRadius(zoom);
  // keep the UI informed of the effective radius
  try { setPlacesRadius(radius); } catch (e) {}
        // Try to render instantly from client cache if available
        let hadCache = false;
        try {
          const key = cacheKeyFor(category, lat, lon, radius);
          const cached = readCache(key);
          if (cached && Array.isArray(cached) && cached.length) {
            hadCache = true;
            // render cached markers immediately (replace existing)
            clearMarkers();
            const newMapCached = {};
            cached.forEach(p => {
              try {
                const plat = p.lat || p.latitude; const plon = p.lon || p.longitude;
                if (!plat || !plon) return;
                const m = L.marker([plat, plon], { title: p.title || p.name }).addTo(mapRef.current);
                const html = `<div style="min-width:180px"><strong>${p.title || ''}</strong><div style="font-size:12px;color:#666">${p.address || ''}</div><div style="margin-top:8px"><a href=\"https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((plat+','+plon))}\" target=\"_blank\">Get directions</a></div></div>`;
                m.bindPopup(html);
                newMapCached[p.providerId || p.id || `${plat}:${plon}`] = m;
              } catch (e) {}
            });
            markersRef.current = newMapCached;
            // fall through to background refresh
          }
        } catch (e) { /* ignore cache read errors */ }

  const url = `${CRM_API_BASE.replace(/\/+$/,'')}/places?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&category=${encodeURIComponent(category)}&radius=${encodeURIComponent(radius)}&limit=100&excludeUnnamed=1`;
        // start network fetch; ensure only latest fetch applies
        const mySeq = ++fetchSeq;
        try { setIsFetching(true); setIsOffline(false); } catch (e) {}
        const res = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('places fetch failed');
        const j = await res.json(); const list = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
        // reflect offline/cached flags returned by backend
        try {
          if (j && j.offline) setIsOffline(true); else setIsOffline(false);
        } catch (e) {}
        if (cancelled) return;

        // If this response is stale (a newer fetch was started), ignore it
        if (mySeq !== fetchSeq) return;

        // If backend reports offline or returned no results but we had cached results,
        // keep the cached markers instead of clearing them (prevents disappearance).
        if ((j && j.offline) && hadCache) {
          try { setIsFetching(false); } catch (e) {}
          return;
        }
        if ((!list || list.length === 0) && hadCache) {
          try { setIsFetching(false); } catch (e) {}
          return;
        }

        // Build and add new markers, then remove old ones. This prevents blank states while fetching.
        const newMap = {};
        list.forEach(p => {
          try {
            const plat = p.lat || p.latitude; const plon = p.lon || p.longitude;
            if (!plat || !plon) return;
            const m = L.marker([plat, plon], { title: p.title || p.name }).addTo(mapRef.current);
            const html = `<div style="min-width:180px"><strong>${p.title || ''}</strong><div style="font-size:12px;color:#666">${p.address || ''}</div><div style="margin-top:8px"><a href=\"https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((plat+','+plon))}\" target=\"_blank\">Get directions</a></div></div>`;
            m.bindPopup(html);
            newMap[p.providerId || p.id || `${plat}:${plon}`] = m;
          } catch (e) {}
        });
        // remove old markers after new ones are added
        const old = markersRef.current || {};
        markersRef.current = newMap;
        try { Object.values(old).forEach(m => { try { mapRef.current.removeLayer(m); } catch (e) {} }); } catch (e) {}

        // cache the fresh result for future quick loads
        try {
          const key = cacheKeyFor(category, lat, lon, radius);
          writeCache(key, list);
        } catch (e) { /* ignore cache write errors */ }
        try { setIsFetching(false); } catch (e) {}
      } catch (e) {
        // If backend signals offline it's expected; avoid noisy console warnings
        try {
          const isOfflineErr = (e && e.offline) || false;
          if (isOfflineErr) {
            try { setIsOffline(true); } catch (er) {}
          } else {
            console.warn('Map places load failed', e && e.message);
          }
          try { setIsFetching(false); } catch (er) {}
        } catch (err) {}
      }
    };
    // On initial mount we want an immediate load; afterwards debounce to avoid rapid fetches
    if (firstRunRef.current) { firstRunRef.current = false; loadPlaces(); } else { scheduleLoad(); }
    // reload when map moves or zoom changes (debounced)
    const onMoveEnd = () => { scheduleLoad(); };
    const onZoomEnd = () => { scheduleLoad(); };
    mapRef.current.on('moveend', onMoveEnd);
    mapRef.current.on('zoomend', onZoomEnd);
    return () => { cancelled = true; try { if (debounceTimer) clearTimeout(debounceTimer); } catch (e) {} if (mapRef.current) { mapRef.current.off('moveend', onMoveEnd); mapRef.current.off('zoomend', onZoomEnd); } };
  }, [category, loading]);

  return (
    <div style={{ maxWidth: 430, margin: '24px auto', border: '1px solid #eee', borderRadius: 12, minHeight: 700, position: 'relative', background: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: 12, display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
        <h3 style={{ margin: 0, fontSize: 18 }}>Map</h3>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="zz-btn flat" onClick={() => { if (navigator && navigator.geolocation) navigator.geolocation.getCurrentPosition(p=>{ const lat=p.coords.latitude, lon=p.coords.longitude; if (mapRef.current) mapRef.current.setView([lat,lon],14); }); }}>Locate me</button>
        </div>
      </div>
      <div id="zz-osm-map" style={{ width: '100%', height: 420 }} />

      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
          {FIXED_PLACE_CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)} style={{ padding: '8px 12px', borderRadius: 20, border: '1px solid #eee', background: category === c ? 'linear-gradient(90deg,#fe8b48,#ff6b6b)' : '#f8f8f8', color: category === c ? '#fff' : '#333', fontWeight: 600 }}>
              {c}
            </button>
          ))}
          <div style={{ marginLeft: 8, alignSelf: 'center', fontSize: 13, color: '#666' }}>{placesRadius} m</div>
        </div>
        {isOffline ? (
          <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: '#FFF6E6', color: '#665225', fontSize: 13 }}>Nearby places temporarily unavailable — showing cached results if available.</div>
        ) : null}

        {/* Loading indicator */}
        {isFetching ? (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 14, height: 14, border: '2px solid #f3f3f3', borderTop: '2px solid #ff6b6b', borderRadius: '50%', animation: 'zz-spin 1s linear infinite' }} aria-hidden="true" />
            <div style={{ fontSize: 13, color: '#666' }}>Finding places…</div>
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          {loading ? <div className="pl-loading">Loading map…</div> : error ? <div className="pl-error">{error}</div> : null}
        </div>
        </div>
        {/* Bottom menu (fixed) */}
        {
          (() => {
            const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
            const isHomeActive = currentPath === '/' || currentPath === '/webapp' || currentPath.includes('/webapp/home');
            const isMapActive = currentPath.includes('/webapp/map');
            const isLocalsActive = false;
            return (
              <nav style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 12, zIndex: 9999, width: 420, maxWidth: '95%', display: 'flex', justifyContent: 'center' }} aria-label="Primary">
                <div style={{ background: 'rgba(255,255,255,0.98)', borderRadius: 28, boxShadow: '0 6px 22px rgba(0,0,0,0.12)', display: 'flex', padding: 8, width: '100%', alignItems: 'center', justifyContent: 'space-around' }}>
                  <button onClick={() => { window.location.href = '/webapp/home'; }} style={{ background: isHomeActive ? 'linear-gradient(90deg,#fe8b48,#ff6b6b)' : 'transparent', border: 'none', color: isHomeActive ? '#fff' : undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', borderRadius: 20, minWidth: 64 }} aria-label="Home">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8.5z" fill={isHomeActive ? '#fff' : '#007AFF'}/></svg>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Home</div>
                  </button>

                  <button onClick={() => { window.location.href = '/webapp/map'; }} style={{ background: isMapActive ? 'linear-gradient(90deg,#fe8b48,#ff6b6b)' : 'transparent', border: 'none', color: isMapActive ? '#fff' : undefined, borderRadius: 20, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }} aria-label="Map">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill={isMapActive ? '#fff' : '#666'}/></svg>
                    Map
                  </button>

          <button onClick={() => {
                      try {
                        // If we're on map, navigate to home then attempt to scroll to places
                        if (window.location.pathname.includes('/webapp/map')) {
                          window.location.href = '/webapp/home#places';
                          // scrolling will be handled when the page loads; we can't reliably scroll across navigations
                        } else {
                        const el = document.querySelector('.pl-places'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('zz-highlight'); setTimeout(()=>el.classList.remove('zz-highlight'), 2000); }
                        }
                      } catch (e) { window.location.href = '/webapp/home'; }
                    }} style={{ background: isLocalsActive ? 'linear-gradient(90deg,#fe8b48,#ff6b6b)' : 'transparent', border: 'none', color: isLocalsActive ? '#fff' : undefined, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', borderRadius: 20, minWidth: 64 }} aria-label="Locals">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5 0-9 2.5-9 5v1h18v-1c0-2.5-4-5-9-5z" fill={isLocalsActive ? '#fff' : '#007AFF'}/></svg>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Locals</div>
                  </button>
                </div>
              </nav>
            );
          })()
        }
    <style>{`@keyframes zz-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
